'use client'

import { useState, useEffect } from 'react'
import styles from './TerminalTabs.module.css'
import { usePathname, useSearchParams } from 'next/navigation'
import { GrowwConnector } from '../GrowwConnector'

/**
 * @uiComponent
 * @name OrderHistory
 * @description The OrderHistory component displays historical trading orders on the Groww platform.
 * It shows completed, cancelled, and rejected orders with detailed information including execution prices,
 * quantities, and timestamps. Features symbol filtering, custom symbol input, and trading statistics
 * like average buy/sell prices and net positions.
 * @location Order History tab in the trading terminal
 * @prop apiKey The API key for Groww connector authentication
 * @prop accessToken Optional access token for enhanced authentication
 */

/**
 * @uiWorkflow
 * @name Review Trading History on Groww
 * @description How to analyze your past trading activity and performance
 * @platform groww
 * @step 1 Navigate to the trading terminal [component: TerminalTabs] [action: navigate]
 * @step 2 Click on the "Order History" tab [component: OrderHistory] [action: click]
 * @step 3 Review all past orders sorted by most recent first [component: OrderHistory] [action: observe]
 * @step 4 Use the symbol dropdown to filter by specific stocks [component: OrderHistory] [action: select]
 * @step 5 Check order status (COMPLETE, FILLED, CANCELLED, REJECTED) [component: OrderHistory] [action: observe]
 * @step 6 Compare order price vs average executed price [component: OrderHistory] [action: observe]
 * @step 7 Review trading statistics in the summary section [component: OrderHistory] [action: observe]
 * @step 8 Use data to analyze trading patterns and performance [component: OrderHistory] [action: analyze]
 */

/**
 * @uiWorkflow
 * @name Analyze Trading Performance by Symbol on Groww
 * @description How to evaluate your trading performance for specific stocks
 * @platform groww
 * @step 1 Open the Order History tab [component: OrderHistory] [action: click]
 * @step 2 Select a specific stock from the symbol dropdown [component: OrderHistory] [action: select]
 * @step 3 Review the trading summary showing buy/sell statistics [component: OrderHistory] [action: observe]
 * @step 4 Check total buy quantity vs total sell quantity [component: OrderHistory] [action: observe]
 * @step 5 Compare average buy price vs average sell price [component: OrderHistory] [action: observe]
 * @step 6 Calculate your net position (buy qty - sell qty) [component: OrderHistory] [action: observe]
 * @step 7 Review individual order execution details [component: OrderHistory] [action: scroll]
 * @step 8 Use this analysis to improve future trading decisions [component: OrderHistory] [action: analyze]
 */

/**
 * @uiWorkflow
 * @name Search Custom Stock History on Groww
 * @description How to view order history for stocks not in the common list
 * @platform groww
 * @step 1 Access the Order History tab [component: OrderHistory] [action: click]
 * @step 2 Click on the symbol dropdown menu [component: OrderHistory] [action: click]
 * @step 3 Select "Custom Symbol" from the dropdown options [component: OrderHistory] [action: select]
 * @step 4 Enter the stock symbol in the custom input field [component: OrderHistory] [action: input] [notes: e.g., RELIANCE, TATAMOTORS]
 * @step 5 Click the "Go" button to search for that symbol's history [component: OrderHistory] [action: click]
 * @step 6 Review the filtered order history for the custom symbol [component: OrderHistory] [action: observe]
 * @step 7 The symbol will be added to your view for future reference [component: OrderHistory] [action: observe]
 */

interface GrowwOrderHistoryItem {
  orderId: string
  symbol: string
  quantity: number
  price: number
  type: string
  side: string
  status: string
  timestamp: number
  averagePrice?: number
  filledQuantity?: number
  exchangeOrderId?: string
  product?: string
  validity?: string
}

// Common stock symbols for the dropdown
const COMMON_SYMBOLS = [
  'ALL ORDERS', // Option to show all orders without filtering
  'RELIANCE',
  'TCS',
  'HDFCBANK',
  'INFY',
  'ICICIBANK',
  'HINDUNILVR',
  'SBIN',
  'BAJFINANCE',
  'BHARTIARTL',
  'KOTAKBANK',
  'WIPRO',
  'AXISBANK',
  'TATAMOTORS',
  'ADANIENT',
  'NIFTY 50'
]

export default function OrderHistory({ apiKey, accessToken }: { apiKey: string, accessToken?: string }) {
  const searchParams = useSearchParams()
  const pathname = usePathname()
  const pathSymbol = pathname ? pathname.split('/').pop()?.toUpperCase() : ''

  const urlSymbol = pathSymbol || searchParams?.get('symbol')

  const [orders, setOrders] = useState<GrowwOrderHistoryItem[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedSymbol, setSelectedSymbol] = useState('ALL ORDERS')
  const [customSymbol, setCustomSymbol] = useState('')
  const [showCustomInput, setShowCustomInput] = useState(false)

  useEffect(() => {
    fetchOrderHistory(selectedSymbol)
  }, [selectedSymbol])

  const fetchOrderHistory = async (symbol: string) => {
    setIsLoading(true)
    setError(null)

    try {
      if (!apiKey) {
        throw new Error('API Key is required')
      }

      // Create an instance of GrowwConnector
      const connector = new GrowwConnector(apiKey)
      
      // if (accessToken) {
      //   connector.setAccessToken(accessToken)
      // }
      
      // Fetch order history from the connector with the specified symbol
      // If 'ALL ORDERS' is selected, pass empty string to get all orders
      const ordersData = await connector.getOrderHistory(symbol === 'ALL ORDERS' ? '' : symbol)

      // Sort orders by timestamp, most recent first
      const sortedOrders = [...ordersData].sort((a, b) => b.timestamp - a.timestamp)
      setOrders(sortedOrders)
    } catch (err: any) {
      console.error('Error fetching order history:', err)
      setError(
        err.message ||
          'Failed to fetch order history. Please ensure you are connected to Groww.'
      )
    } finally {
      setIsLoading(false)
    }
  }

  const handleSymbolChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value
    if (value === 'custom') {
      setShowCustomInput(true)
    } else {
      setShowCustomInput(false)
      setSelectedSymbol(value)
    }
  }

  const handleCustomSymbolSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (customSymbol) {
      setSelectedSymbol(customSymbol.toUpperCase())
      setCustomSymbol('')
      setShowCustomInput(false)
    }
  }

  if (isLoading)
    return <div className={styles.loadingState}>Loading order history...</div>
  if (error) return <div className={styles.errorState}>{error}</div>

  return (
    <div className={styles.ordersContainer}>
      <div className={styles.symbolSelector}>
        <div className={styles.selectorWrapper}>
          <select
            value={
              showCustomInput
                ? 'custom'
                : COMMON_SYMBOLS.includes(selectedSymbol)
                  ? selectedSymbol
                  : 'custom'
            }
            onChange={handleSymbolChange}
            className={styles.symbolDropdown}
          >
            {COMMON_SYMBOLS.map(symbol => (
              <option key={symbol} value={symbol}>
                {symbol}
              </option>
            ))}
            <option value="custom">Custom Symbol</option>
          </select>

          {(showCustomInput || !COMMON_SYMBOLS.includes(selectedSymbol)) && (
            <form
              onSubmit={handleCustomSymbolSubmit}
              className={styles.customSymbolForm}
            >
              <input
                type="text"
                value={
                  customSymbol ||
                  (!COMMON_SYMBOLS.includes(selectedSymbol) && !showCustomInput
                    ? selectedSymbol
                    : '')
                }
                onChange={e => setCustomSymbol(e.target.value)}
                placeholder="Enter symbol (e.g. RELIANCE)"
                className={styles.customSymbolInput}
                autoFocus
              />
              <button type="submit" className={styles.submitButton}>
                Go
              </button>
            </form>
          )}
        </div>
        <div className={styles.currentSymbol}>
          {(() => {
            // Calculate metrics for the selected symbol
            const filledOrders = orders.filter(
              order => order.status === 'COMPLETE' || order.status === 'FILLED'
            )

            // Calculate total buy and sell quantities
            const buyOrders = filledOrders.filter(order => order.side === 'BUY')
            const sellOrders = filledOrders.filter(
              order => order.side === 'SELL'
            )

            const totalBuyQty = buyOrders.reduce(
              (sum, order) => sum + (order.filledQuantity || order.quantity),
              0
            )
            const totalSellQty = sellOrders.reduce(
              (sum, order) => sum + (order.filledQuantity || order.quantity),
              0
            )

            // Calculate total buy and sell values
            const totalBuyValue = buyOrders.reduce(
              (sum, order) => sum + (order.filledQuantity || order.quantity) * (order.averagePrice || order.price),
              0
            )
            const totalSellValue = sellOrders.reduce(
              (sum, order) => sum + (order.filledQuantity || order.quantity) * (order.averagePrice || order.price),
              0
            )

            // Calculate average prices
            const avgBuyPrice =
              totalBuyQty > 0 ? totalBuyValue / totalBuyQty : 0
            const avgSellPrice =
              totalSellQty > 0 ? totalSellValue / totalSellQty : 0

            // Calculate overall average price
            const totalQty = totalBuyQty + totalSellQty
            const totalValue = totalBuyValue + totalSellValue
            const avgPrice = totalQty > 0 ? totalValue / totalQty : 0

            return (
              <>
                Current: <strong>{selectedSymbol}</strong>
                {filledOrders.length > 0 && (
                  <>
                    {' | '}
                    Total Buy:{' '}
                    <strong className={styles.buyOrder}>
                      {totalBuyQty.toFixed(2)}
                    </strong>{' '}
                    (₹{totalBuyValue.toFixed(2)}){' | '}
                    Total Sell:{' '}
                    <strong className={styles.sellOrder}>
                      {totalSellQty.toFixed(2)}
                    </strong>{' '}
                    (₹{totalSellValue.toFixed(2)}){' | '}
                    Avg Price:{' '}
                    <strong>
                      ₹{avgPrice.toFixed(2)}
                    </strong>
                    {' | '}
                    Net:{' '}
                    <strong
                      className={
                        totalBuyQty > totalSellQty
                          ? styles.buyOrder
                          : styles.sellOrder
                      }
                    >
                      {Math.abs(totalBuyQty - totalSellQty).toFixed(2)}
                    </strong>
                  </>
                )}
              </>
            )
          })()}
        </div>
      </div>

      {orders.length === 0 ? (
        <div className={styles.emptyState}>
          No order history found {selectedSymbol === 'ALL ORDERS' ? '' : `for ${selectedSymbol}`}
        </div>
      ) : (
        <div className={styles.ordersTable}>
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Symbol</th>
                <th>Type</th>
                <th>Side</th>
                <th>Price</th>
                <th>Quantity</th>
                <th>Filled</th>
                <th>Total</th>
                <th>Product</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {orders.map(order => {
                const date = new Date(order.timestamp)
                const formattedDate = date.toLocaleString()

                // Use average price if available, otherwise use order price
                const price = order.averagePrice || order.price
                const quantity = order.quantity
                const filled = order.filledQuantity || 0

                // Calculate total value
                const total = price * (filled > 0 ? filled : quantity)

                return (
                  <tr key={order.orderId}>
                    <td>{formattedDate}</td>
                    <td>{order.symbol}</td>
                    <td>{order.type}</td>
                    <td
                      className={
                        order.side === 'BUY'
                          ? styles.buyOrder
                          : styles.sellOrder
                      }
                    >
                      {order.side}
                    </td>
                    <td>₹{price.toFixed(2)}</td>
                    <td>{quantity}</td>
                    <td>{filled}</td>
                    <td>₹{total.toFixed(2)}</td>
                    <td>{order.product || '-'}</td>
                    <td
                      className={
                        order.status === 'COMPLETE' || order.status === 'FILLED'
                          ? styles.statusFilled
                          : order.status === 'CANCELLED' || order.status === 'REJECTED'
                            ? styles.statusCanceled
                            : styles.statusOther
                      }
                    >
                      {order.status}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
